// src/pages/document/DocumentVersionDetailPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';

const STATUS_VARIANTS: Record<string, string> = {
  DOCUMENT_STATUS_DRAFT: 'secondary',
  DOCUMENT_STATUS_REVIEW: 'warning',
  DOCUMENT_STATUS_APPROVED: 'success',
  DOCUMENT_STATUS_PUBLISHED: 'success',
  DOCUMENT_STATUS_ARCHIVED: 'secondary',
  DOCUMENT_STATUS_REJECTED: 'error',
};

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

function formatJson(value: any): string {
  if (!value) return '--';
  try {
    return typeof value === 'string' ? JSON.stringify(JSON.parse(value), null, 2) : JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

/**
 * Document version detail page.
 */
export const DocumentVersionDetailPage = observer(() => {
  const { id, versionId } = useParams<{ id: string; versionId: string }>();
  const store = useDocument();
  const { t } = useI18n();
  const [item, setItem] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id || !versionId) return;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await store.GetVersion('document', id, versionId);
        setItem(res?.version || res);
      } catch (err: any) {
        setError(err?.message || 'Failed to load version');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id, versionId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <ErrorAlert error={error} />
        <Link to={`/documents/${id}/versions`} className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to={`/documents/${id}/versions`} className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/documents" className="text-muted-foreground hover:text-foreground">
            {t('document.list')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}`} className="text-muted-foreground hover:text-foreground">
            {t('document.details')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <Link to={`/documents/${id}/versions`} className="text-muted-foreground hover:text-foreground">
            {t('document.versions')}
          </Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">{t('document.versionDetails')}</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">{t('document.versionDetails')}</h1>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('document.versionDetails')}</CardTitle>
          <CardDescription>
            {item.version_label || `Version ${item.version_number ?? ''}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Version Number</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.version_number ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Version Label</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.version_label ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Title</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.title ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Status</dt>
              <dd className="text-sm text-foreground col-span-2">
                <Badge
                  variant={(STATUS_VARIANTS[item.status] || 'secondary') as any}
                  size="sm"
                >
                  {String(item.status ?? '').replace('DOCUMENT_STATUS_', '') || '--'}
                </Badge>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Change Summary</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String(item.change_summary ?? '')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Snapshot</dt>
              <dd className="text-sm text-foreground col-span-2">
                <pre className="bg-muted rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap">
                  {formatJson(item.snapshot)}
                </pre>
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Approved By</dt>
              <dd className="text-sm text-foreground col-span-2">
                {item.approved_by?.display_name || item.approved_by?.name || String(item.approved_by ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Approved At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate(item.approved_at)}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Attachment</dt>
              <dd className="text-sm text-foreground col-span-2">
                {String((item.attachment?.id || item.attachment) ?? '--')}
              </dd>
            </div>
            <div className="py-3 grid grid-cols-3 gap-4">
              <dt className="text-sm font-medium text-muted-foreground">Created At</dt>
              <dd className="text-sm text-foreground col-span-2">
                {formatDate(item.created_at)}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
});

export default DocumentVersionDetailPage;
